import {
  colorRGBValuesTriplet,
  ExhibitorTicker,
  TicketTypeItemType,
  UserTicket,
} from "@ac-expo/api-types";
import { CustomPosterFormat } from "@prisma/client";
import { orderBy } from "lodash";

export function utils(): string {
  return "utils";
}

export function valueIfExist<T>(value: T | null | undefined) {
  return value ?? undefined;
}

export function isNullish<T>(
  value: T | null | undefined
): value is null | undefined {
  return value === null || value === undefined;
}

export function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

export async function runBatchById<TInput extends { id: string }, TOutput>(
  items: TInput[],
  op: (item: TInput) => Promise<TOutput>
): Promise<Map<string, TOutput>> {
  const entriesPromises = items.map(
    (i) => async () => [i.id, await op(i)] as const
  );

  const entries = await Promise.all(entriesPromises.map((p) => p()));
  return new Map(entries);
}

export function isRGBColor(color: string): boolean {
  if (color in colorRGBValuesTriplet) return true;

  const rgbRegex = /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/;
  const rgbaRegex =
    /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(0|1|0?\.\d+)\)$/;

  const rgbMatch = color.match(rgbRegex);
  const rgbaMatch = color.match(rgbaRegex);

  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return Number(r) <= 255 && Number(g) <= 255 && Number(b) <= 255;
  }

  if (rgbaMatch) {
    const [, r, g, b] = rgbaMatch;
    return Number(r) <= 255 && Number(g) <= 255 && Number(b) <= 255;
  }

  return false;
}

export function formatRGBColor(color: string): string {
  if (!(color in colorRGBValuesTriplet)) return color;
  const rgbArray = colorRGBValuesTriplet[color];
  return `rgb(${rgbArray[0]},${rgbArray[1]},${rgbArray[2]})`;
}

export function formatHexaColor(color: string): string {
  if (!(color in colorRGBValuesTriplet)) return color;
  const rgbArray = colorRGBValuesTriplet[color];
  const hexaArray = rgbArray.map(function (x) {
    const xHexa = x.toString(16);
    return xHexa.length == 1 ? "0" + xHexa : xHexa;
  });
  return "#" + hexaArray.join("");
}

export const hexaToRgb = (hexa: string) => {
  const numericValue = parseInt(hexa.slice(1), 16);
  const r = (numericValue >> 16) & 0xff;
  const g = (numericValue >> 8) & 0xff;
  const b = numericValue & 0xff;
  return { r, g, b };
};

export const ageToAgeGroup = (age: number | null) => {
  if (!age) return "-";
  if (age < 20) return "-20";
  if (age < 26) return "20 - 25";
  if (age < 36) return "25 - 35";
  return "+35";
};

export enum CustomBlockColor {
  DARKGRAY = "#373530",
  GRAY = "#787774",
  BROWN = "#976D57",
  ORANGE = "#CC782F",
  BEIGE = "#C29343",
  GREEN = "#548164",
  BLUE = "#487CA5",
  PURPLE = "#8A67AB",
  PINK = "#B35488",
  RED = "#C4554D",
}

export type SimpleBlock = {
  id: string;
  isBadge: boolean;
  color: string;
  isHalf: boolean;
  x: number;
  y: number;
  page: number;
};

export type Block = SimpleBlock & {
  name: string;
  price: number;
};

export function getBlockWidth(block: Block): number {
  return block.isHalf ? 1 : 2;
}

export function isOverlapping(block1: Block, block2: Block) {
  if (block1.y !== block2.y || block1.page !== block2.page) return false;
  const start1 = block1.x;
  const end1 = block1.x + getBlockWidth(block1) - 1;
  const start2 = block2.x;
  const end2 = block2.x + getBlockWidth(block2) - 1;
  return start1 <= end2 && start2 <= end1;
}

export const GRID_WIDTH = 6;
export const GRID_ROWS = 6;

export function findNextSpot(
  width: number,
  initialX: number,
  initialY: number,
  initialPage: number
): { x: number; y: number; page: number } {
  let y = initialY;
  let x = initialX;
  let page = initialPage;
  if (x > GRID_WIDTH - width) {
    y++;
    x = 0;
  }
  if (y >= GRID_ROWS || (y === GRID_ROWS - 1 && x >= GRID_WIDTH - width - 1)) {
    x = 0;
    y = 0;
    page++;
  }
  return { x, y, page };
}

export function findLastSpot(
  blocks: SimpleBlock[],
  width: number
): { x: number; y: number; page: number } {
  const lastPageBlock = orderBy(
    blocks,
    ["page", "y", "x"],
    ["desc", "desc", "desc"]
  )?.[0] ?? {
    x: -1,
    y: 0,
    isHalf: true,
    page: 0,
  };
  return findNextSpot(
    width,
    lastPageBlock.x + (lastPageBlock.isHalf ? 1 : 2),
    lastPageBlock.y,
    lastPageBlock.page
  );
}

export function findLastPageSpot(
  blocks: SimpleBlock[],
  width: number,
  page: number
): { x: number; y: number; page: number } {
  const lastPageBlock = orderBy(
    blocks.filter((b) => b.page === page),
    ["y", "x"],
    ["desc", "desc"]
  )?.[0] ?? { x: -1, y: 0, isHalf: true, page };
  const afterLastPageBlockPos = findNextSpot(
    width,
    lastPageBlock.x + (lastPageBlock.isHalf ? 1 : 2),
    lastPageBlock.y,
    lastPageBlock.page
  );
  if (afterLastPageBlockPos.page === page) return afterLastPageBlockPos;
  return {
    page,
    x: GRID_WIDTH - 2 - width,
    y: GRID_ROWS - 1,
  };
}

export function pushBlocks(blocks: Block[], movedBlock: Block): Block[] {
  let initialBlocks = blocks.filter((b) => b.id !== movedBlock.id);
  const finalPositions: Block[] = [];
  const queue: Block[] = [movedBlock];
  let currentX = movedBlock.x;
  let currentY = movedBlock.y;
  let currentPage = movedBlock.page;

  while (queue.length > 0) {
    let current = queue.shift()!;
    const pos = findNextSpot(
      getBlockWidth(current),
      currentX,
      currentY,
      currentPage
    );
    current = { ...current, x: pos.x, y: pos.y, page: pos.page };

    const collisionBlocks = initialBlocks
      .filter((b) => isOverlapping(b, current))
      .sort((a, b) => (a.x > b.x ? 1 : -1));

    collisionBlocks.forEach((block) => {
      queue.push(block);
      initialBlocks = initialBlocks.filter((b) => b.id !== block.id);
    });

    finalPositions.push(current);

    currentX = current.x + getBlockWidth(current);
    currentY = current.y;
    currentPage = current.page;
  }

  return [...finalPositions, ...initialBlocks];
}

export function changeBlockPage(
  blocks: Block[],
  currentBlock: Block,
  page: number
): Block[] {
  if (currentBlock.page === page) return blocks;
  const initialBlocks = blocks.filter((b) => b.id !== currentBlock.id);
  const newPos = findLastPageSpot(
    initialBlocks,
    getBlockWidth(currentBlock),
    page
  );
  const movedBlock = {
    ...currentBlock,
    x: newPos.x,
    y: newPos.y,
    page: newPos.page,
  };
  return pushBlocks(initialBlocks, movedBlock);
}

export const formatTicketData = (
  tickets: UserTicket[],
  ticketTypes: TicketTypeItemType[]
): ExhibitorTicker[] => {
  const ticketData = ticketTypes.reduce((acc, ticketType) => {
    acc[ticketType.id] = {
      name: ticketType.name,
      activityId: ticketType.activityId,
      sentTickets: 0,
      leftTickets: ticketType.isQuantityInfinite
        ? Infinity
        : ticketType.quantityLeft || 0,
    };
    return acc;
  }, {} as any);

  tickets.forEach((ticket) => {
    const { ticketTypeId, userId, givenToVisitorId, givenToCommercialId } =
      ticket;
    if (!ticketTypeId) {
      return;
    }

    if (ticketData[ticketTypeId]) {
      if (userId || givenToVisitorId || givenToCommercialId) {
        ticketData[ticketTypeId].sentTickets += 1;
        if (ticketData[ticketTypeId].leftTickets !== Infinity) {
          const quantity = ticketData[ticketTypeId].leftTickets;
          ticketData[ticketTypeId].leftTickets = quantity;
        }
      }
    } else {
      const ticketsLeft = tickets.filter(
        (t) =>
          t.ticketTypeId === ticketTypeId &&
          !t.userId &&
          !t.givenToVisitorId &&
          !t.givenToCommercialId
      ).length;
      ticketData[ticketTypeId] = {
        id: ticket.ticketTypeId,
        name: ticket.ticketType.name,
        sentTickets: userId || givenToVisitorId || givenToCommercialId ? 1 : 0,
        leftTickets: ticketsLeft,
        activityId: ticket.ticketType.activityId,
      };
    }
  });

  return Object.values(ticketData);
};

export const getLeftTickets = (
  tickets: UserTicket[],
  ticketTypes: TicketTypeItemType[]
): number => {
  return formatTicketData(tickets, ticketTypes).reduce(
    (acc, curr) => acc + (isFinite(curr.leftTickets) ? curr.leftTickets : 0),
    0
  );
};

export const CurrencyValues = {
  oneCent: 1,
  twoCent: 2,
  fiveCent: 5,
  tenCent: 10,
  twentyCent: 20,
  fiftyCent: 50,
  oneEuro: 100,
  twoEuro: 200,
  fiveEuro: 500,
  tenEuro: 1000,
  twentyEuro: 2000,
  fiftyEuro: 5000,
  oneHundredEuro: 10000,
  twoHundredEuro: 20000,
  fiveHundredEuro: 50000,
} as const;

export type CurrencyKey = keyof typeof CurrencyValues;

export const currencyKeys = Object.keys(CurrencyValues) as CurrencyKey[];

export function formatToCssRatio(format: CustomPosterFormat): string {
  switch (format) {
    case "format4x3":
      return "4/3";
    case "format16x9":
      return "16/9";
    case "format21x9":
      return "21/9";
  }
}
